/**
 * Phase INTL-1 — Location data for Prospect Registration.
 * Phase INTL-2 — Swapped 55-country hand-list for full ISO 3166-1 set with
 *               flag emoji prefixes + searchable Combobox.
 *
 * Used by:
 *   - AthleteDashboard.jsx (Prospect Registration form)
 *   - Public + Dashboard location displays (formatLocation)
 */
import { usStates as US_STATE_FULL_LIST } from './utils';
import { ISO_COUNTRIES, flagFor, flagForCountryName, COUNTRY_NAME_TO_CODE } from './countriesISO';

// ─────────────────────────────────────────────────────────────────────────────
// Countries — full ISO 3166-1 list + "Other / Not Listed" fallback.
// `COUNTRIES` is an array of plain strings for backward compatibility with
// any code that still iterates the legacy list. New UI uses `COUNTRY_OPTIONS`
// (objects with code + flag) for the searchable Combobox.
// ─────────────────────────────────────────────────────────────────────────────
export const COUNTRY_OPTIONS = [
  ...ISO_COUNTRIES.map((c) => ({ code: c.code, name: c.name, flag: flagFor(c.code) })),
  // Fallback sentinel — region field becomes free-text, no flag emoji.
  { code: '', name: 'Other / Not Listed', flag: '' },
];

export const COUNTRIES = COUNTRY_OPTIONS.map((c) => c.name);

// Re-export so other modules can render flags from a stored country name.
export { flagFor, flagForCountryName, COUNTRY_NAME_TO_CODE };

// ─────────────────────────────────────────────────────────────────────────────
// Full US state names (reuses canonical list from lib/utils.js).
// ─────────────────────────────────────────────────────────────────────────────
export const US_STATES = US_STATE_FULL_LIST;

// Canadian provinces + territories.
export const CA_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
];

// 2-letter postal abbreviations for fallback display when older prospects
// stored values like "NY", "CA", "TX" rather than "New York". Used by
// `formatLocation` to recognize US states even when country is blank.
export const US_STATE_ABBREVIATIONS = new Set([
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
]);

// Lower-case set of full US state names — used together with the abbrev set
// to recognize legacy US-only rows that have no country value yet.
export const US_STATE_NAMES_LOWER = new Set(US_STATES.map((s) => s.toLowerCase()));

/**
 * Returns the appropriate region options for a given country, or null when
 * the form should fall through to a free-text input.
 *
 *   getRegionsForCountry('United States')      -> ['Alabama', ..., 'Wyoming']
 *   getRegionsForCountry('Canada')             -> ['Alberta', ..., 'Yukon']
 *   getRegionsForCountry('Germany')            -> null   (free-text)
 *   getRegionsForCountry('Other / Not Listed') -> null   (free-text)
 *   getRegionsForCountry('')                   -> null
 */
export function getRegionsForCountry(country) {
  if (!country) return null;
  if (country === 'United States') return US_STATES;
  if (country === 'Canada') return CA_PROVINCES;
  return null;
}

/**
 * The form should require a region selection only when the country has a
 * canonical list of subdivisions (US / Canada). For free-text countries the
 * field is optional per spec.
 */
export function isRegionRequired(country) {
  return getRegionsForCountry(country) !== null;
}

/**
 * Public location formatter — used everywhere we render a prospect's location.
 *
 *   formatLocation({ city: 'Rochester', state: 'New York', country: 'United States' })
 *     → 'Rochester, New York, United States'
 *
 *   formatLocation({ city: 'Toronto', state: 'Ontario', country: 'Canada' })
 *     → 'Toronto, Ontario, Canada'
 *
 *   formatLocation({ city: 'Lagos', state: '', country: 'Nigeria' })
 *     → 'Lagos, Nigeria'   (region blank → omit)
 *
 *   formatLocation({ city: 'Rochester', state: 'NY' })   ← legacy row, no country
 *     → 'Rochester, NY, United States'
 *      (US recognized by state abbreviation → country backfilled for display)
 */
export function formatLocation({ city, state, country } = {}, options = {}) {
  const c = (city || '').trim();
  const s = (state || '').trim();
  let cn = (country || '').trim();

  // Backfill country for legacy US-only rows so display stays correct.
  if (!cn && s) {
    if (
      US_STATE_ABBREVIATIONS.has(s.toUpperCase()) ||
      US_STATE_NAMES_LOWER.has(s.toLowerCase())
    ) {
      cn = 'United States';
    }
  }

  const base = [c, s, cn].filter(Boolean).join(', ');
  // Phase INTL-2 — opt-in flag emoji prefix. Display surfaces that want a
  // visible flag pass `{ flag: true }`; everywhere else the plain comma-
  // separated string is preserved for backward compatibility.
  if (options.flag && cn) {
    const fl = flagForCountryName(cn);
    if (fl) return `${fl} ${base}`;
  }
  return base;
}

/**
 * Compact display for tight surfaces (cards, search dropdown). Drops the
 * city and renders just `state, country` (or just country if state blank).
 * With `{ flag: true }` the flag emoji prefixes the line.
 *
 *   formatLocationCompact({ city:'Lagos', state:'Lagos State', country:'Nigeria' })
 *     → 'Lagos State, Nigeria'
 *
 *   formatLocationCompact(…, { flag: true })
 *     → '🇳🇬 Lagos State, Nigeria'
 */
export function formatLocationCompact({ state, country, city } = {}, options = {}) {
  const s = (state || '').trim();
  let cn = (country || '').trim();
  // Apply the same legacy US backfill so old prospects stay correct.
  if (!cn && s) {
    if (
      US_STATE_ABBREVIATIONS.has(s.toUpperCase()) ||
      US_STATE_NAMES_LOWER.has(s.toLowerCase())
    ) {
      cn = 'United States';
    }
  }
  const base = [s, cn].filter(Boolean).join(', ') || (city || '').trim();
  if (!base) return '';
  if (options.flag && cn) {
    const fl = flagForCountryName(cn);
    if (fl) return `${fl} ${base}`;
  }
  return base;
}
